import { useState, useEffect } from 'react';
import { Order } from '../../dto/Profile.dto';
import axios from 'axios';
import './styles/BillSummary.css';
import './styles/BillSummaryMobile.css';

const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 1024; // Covers mobile, tablet, and iPad
};

interface BillSummaryProps {
  order: Order | null;
}

const BillSummary: React.FC<BillSummaryProps> = ({ order }) => {
  const [, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    
    // Set initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleDownloadInvoice = async () => {
    if (!order) return;

    try {
      // Get auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please login to download invoice');
        return;
      }

      // Create axios instance with auth header
      const axiosInstance = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL || 'https://backend.buildsupplyco.com/api/v1',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Download invoice
      const response = await axiosInstance.get(`/orders/${order._id}/invoice`);

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${order.orderId || order._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 404) {
        alert('Order not found.');
      } else {
        alert('Failed to download invoice. Please try again.');
      }
    }
  };

  return (
    <div className="bill-summary-wrapper">
      {/* Ship To Card */}
      <div className="bill-card">
        <h2 className="bill-card-title">Ship To</h2>
        <div className="bill-divider"></div>
        <p className="bill-name">{order?.customerName || 'Name not available'}</p>
        <p className="bill-phone">{order?.customerPhone || 'Phone not available'}</p>
        <p className="bill-address">
          {order?.shippingAddress?.primary_address_line || 'Address not available'}
          {order?.shippingAddress?.secondary_address_line && `, ${order.shippingAddress.secondary_address_line}`}
          {order?.shippingAddress?.locality && `, ${order.shippingAddress.locality}`}
          {order?.shippingAddress?.landmark && `, ${order.shippingAddress.landmark}`}
        </p>
      </div>

      {/* Bill Summary Card */}
      <div className="bill-card">
        <h2 className="bill-card-title">Bill Summary</h2>
        <div className="bill-divider"></div>
        <div className="bill-row">
          <span className="bill-label">Sub Total</span>
          <span className="bill-value">₹ {((order?.subtotal || 0) + (order?.totalGST || 0)).toFixed(2)}</span>
        </div>
        {(order?.shippingCost ?? 0) > 0 && (
          <div className="bill-row">
            <span className="bill-label">Delivery</span>
            <span className="bill-value">₹ {order?.shippingCost?.toFixed(2)}</span>
          </div>
        )}
        {(order?.couponDiscount ?? 0) > 0 && (
          <div className="bill-row">
            <span className="bill-label">Coupon Discount</span>
            <span className="bill-value">-₹ {order?.couponDiscount?.toFixed(2)}</span>
          </div>
        )}
        {(order?.appliedCredits ?? 0) > 0 && (
          <div className="bill-row">
            <span className="bill-label">Credits Used</span>
            <span className="bill-value">-₹ {order?.appliedCredits?.toFixed(2)}</span>
          </div>
        )}
        <div className="bill-dotted-divider"></div>
        <div className="bill-row bill-total-row">
          <span className="bill-label-total">Total Bill</span>
          <span className="bill-value-total">₹ {order?.totalAmount?.toFixed(2) || '0.00'}</span>
        </div>
        <div className="bill-payment-box">
          <span className="bill-payment-label">Payment Method</span>
          <span className="bill-payment-value">
            {order?.paymentMode === 'COD' ? 'Cash on Delivery' : 
             order?.paymentMode === 'UPI' ? 'BHIM UPI' : 
             order?.paymentMode || 'N/A'}
          </span>
        </div>
        <button className="bill-download-btn" onClick={handleDownloadInvoice}>Download Invoice</button>
      </div>

      {/* Coins Earned Card */}
      {/* <div className="bill-card">
        <div 
          className="bill-coins-header" 
          onClick={() => setIsCoinsExpanded(!isCoinsExpanded)}
        >
          <h2 className="bill-card-title">Coins Earned</h2>
          <span className={`bill-collapse-icon ${isCoinsExpanded ? 'expanded' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
        {isCoinsExpanded && (
          <>
            <div className="bill-divider"></div>
            <p className="bill-coins-text">On this order you have earned ₹50 coins</p>
          </>
        )}
      </div> */}
    </div>
  );
};

export default BillSummary;